import { AsyncSubject, Observable } from 'rxjs';
import axios, {
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
  Method,
} from 'axios';
import authService from './authService';

interface HttpRequest {
  method: Method;
  url: string;
  headers?: AxiosRequestHeaders;
  data?: any;
}

class HttpService {
  public getJSON(url: string): Observable<any> {
    const subject = new AsyncSubject<any>();

    const request: HttpRequest = {
      method: 'GET',
      url,
    };
    this.makeRequest(request, subject);

    return subject;
  }

  public postJSON(
    url: string,
    data: any,
    formData: boolean = false,
  ): Observable<any> {
    const subject = new AsyncSubject<any>();

    const request: HttpRequest = {
      method: 'POST',
      url,
      data,
    };
    this.makeRequest(request, subject, formData);

    return subject;
  }

  public putJSON(url: string, data: any): Observable<any> {
    const subject = new AsyncSubject<any>();

    const request: HttpRequest = {
      method: 'PUT',
      url,
      data,
    };
    this.makeRequest(request, subject);

    return subject;
  }

  public patchJSON(url: string, data: any): Observable<any> {
    const subject = new AsyncSubject<any>();

    const request: HttpRequest = {
      method: 'PATCH',
      url,
      data,
    };
    this.makeRequest(request, subject);

    return subject;
  }

  public deleteJSON(url: string): Observable<any> {
    const subject = new AsyncSubject<any>();

    const request: HttpRequest = {
      method: 'DELETE',
      url,
    };
    this.makeRequest(request, subject);

    return subject;
  }

  private makeRequest(
    request: HttpRequest,
    subject: AsyncSubject<AxiosResponse>,
    formData: boolean = false,
  ) {
    this.getOptions(request, formData).then((options) => {
      axios
        .request(options)
        .then((resp) => {
          this.resolveRequest(resp, subject);
        })
        .catch((err) => {
          if (err.response?.status === 401) {
            authService.logout();
          }
          this.resolveRequest(err.response, subject);
        });
    });
  }

  private resolveRequest(response: AxiosResponse, subject: AsyncSubject<any>) {
    if (this.isValid(response)) {
      const data = response.data.data ? response.data.data : response.data;
      subject.next(data);
      subject.complete();
    } else {
      subject.error(response?.data || 'HTTP request failed');
    }
  }

  private getOptions(
    request: HttpRequest,
    formData: boolean,
  ): Promise<AxiosRequestConfig> {
    return new Promise((resolve) => {
      this.getHeaders(formData).then((headers) => {
        const options: AxiosRequestConfig = {
          method: request.method,
          url: request.url,
          headers,
          data: request.data ? request.data : undefined,
        };
        resolve(options);
      });
    });
  }

  private getHeaders(formData: boolean): Promise<any> {
    const { __environment__ } = window as any;
    return new Promise((done) => {
      const newHeaders = {
        Accept: 'application/json',
        'X-App-Bc': __environment__.REACT_APP_CLIENT_BC?.trim(),
        'Content-Type': formData
          ? 'multipart/form-data'
          : 'application/json;charset=UTF-8',
      };

      authService.getTokens().subscribe((tokens) => {
        if (tokens.accessToken) {
          (newHeaders as any).Authorization = `Bearer ${tokens.accessToken}`;
        }
        if (tokens.idToken) {
          (newHeaders as any).AuthorizationID = `${tokens.idToken}`;
        }

        done(newHeaders);
      });
    });
  }

  private isValid(response: AxiosResponse): boolean {
    return response?.status >= 200 && response?.status < 300;
  }
}

const httpService = new HttpService();

export default httpService;
